import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Switch,
  FormControlLabel,
  FormGroup,
  Checkbox,
  FormLabel,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Settings,
  Webhook,
  PlayArrow,
  Link,
  CheckCircle,
  Error,
  Info
} from '@mui/icons-material';
import api from '../services/api';

interface IntegrationApp {
  id: string;
  name: string;
  description: string;
  logo_url?: string;
  category: string;
  enabled: boolean;
  webhook_support: boolean;
  supported_events: string[];
}

interface WebhookHook {
  id: number;
  hook_type: string;
  app_id?: string;
  webhook_url?: string;
  events: string[];
  active: boolean;
  created_at: string;
  updated_at: string;
}

interface TestResult {
  success: boolean;
  status_code?: number;
  response_body?: string;
  error?: string;
  message: string;
}

const IntegrationsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [apps, setApps] = useState<IntegrationApp[]>([]);
  const [hooks, setHooks] = useState<WebhookHook[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingHook, setEditingHook] = useState<WebhookHook | null>(null);
  const [selectedApp, setSelectedApp] = useState<IntegrationApp | null>(null);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const [formData, setFormData] = useState({
    webhook_url: '',
    events: [] as string[],
    app_id: '',
    settings: {}
  });

  const availableEvents = [
    { id: 'conversation_created', label: 'Conversation Created', category: 'Conversations' },
    { id: 'conversation_updated', label: 'Conversation Updated', category: 'Conversations' },
    { id: 'conversation_resolved', label: 'Conversation Resolved', category: 'Conversations' },
    { id: 'conversation_reopened', label: 'Conversation Reopened', category: 'Conversations' },
    { id: 'conversation_status_changed', label: 'Status Changed', category: 'Conversations' },
    { id: 'conversation_assigned', label: 'Conversation Assigned', category: 'Conversations' },
    { id: 'conversation_unassigned', label: 'Conversation Unassigned', category: 'Conversations' },
    { id: 'message_created', label: 'Message Created', category: 'Messages' },
    { id: 'message_updated', label: 'Message Updated', category: 'Messages' },
    { id: 'contact_created', label: 'Contact Created', category: 'Contacts' },
    { id: 'contact_updated', label: 'Contact Updated', category: 'Contacts' },
    { id: 'agent_added', label: 'Agent Added', category: 'Agents' },
    { id: 'agent_removed', label: 'Agent Removed', category: 'Agents' }
  ];

  const loadApps = async () => {
    try {
      const response = await api.get('/integrations/apps');
      setApps(response.data);
    } catch (error) {
      console.error('Failed to load integration apps:', error);
      setSnackbar({ open: true, message: 'Failed to load integration apps', severity: 'error' });
    }
  };

  const loadHooks = async () => {
    try {
      setLoading(true);
      const response = await api.get('/integrations/hooks');
      setHooks(response.data);
    } catch (error) {
      console.error('Failed to load integration hooks:', error);
      setSnackbar({ open: true, message: 'Failed to load integration hooks', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateHook = async () => {
    try {
      const response = await api.post('/integrations/hooks', formData);
      setHooks([...hooks, response.data]);
      setDialogOpen(false);
      resetForm();
      setSnackbar({ open: true, message: 'Integration hook created successfully', severity: 'success' });
    } catch (error) {
      console.error('Failed to create integration hook:', error);
      setSnackbar({ open: true, message: 'Failed to create integration hook', severity: 'error' });
    }
  };

  const handleUpdateHook = async () => {
    if (!editingHook) return;

    try {
      const response = await api.put(`/integrations/hooks/${editingHook.id}`, formData);
      setHooks(hooks.map(hook => hook.id === editingHook.id ? response.data : hook));
      setDialogOpen(false);
      setEditingHook(null);
      resetForm();
      setSnackbar({ open: true, message: 'Integration hook updated successfully', severity: 'success' });
    } catch (error) {
      console.error('Failed to update integration hook:', error);
      setSnackbar({ open: true, message: 'Failed to update integration hook', severity: 'error' });
    }
  };

  const handleDeleteHook = async (hookId: number) => {
    if (!window.confirm('Are you sure you want to delete this integration hook?')) return;

    try {
      await api.delete(`/integrations/hooks/${hookId}`);
      setHooks(hooks.filter(hook => hook.id !== hookId));
      setSnackbar({ open: true, message: 'Integration hook deleted successfully', severity: 'success' });
    } catch (error) {
      console.error('Failed to delete integration hook:', error);
      setSnackbar({ open: true, message: 'Failed to delete integration hook', severity: 'error' });
    }
  };

  const handleTestHook = async (hookId: number) => {
    try {
      const response = await api.post(`/integrations/hooks/${hookId}/test`);
      setTestResult(response.data);
      setSnackbar({ 
        open: true, 
        message: response.data.success ? 'Test webhook sent successfully' : 'Test webhook failed',
        severity: response.data.success ? 'success' : 'error'
      });
    } catch (error) {
      console.error('Failed to test integration hook:', error);
      setSnackbar({ open: true, message: 'Failed to test integration hook', severity: 'error' });
    }
  };

  const openCreateDialog = (app?: IntegrationApp) => {
    resetForm();
    if (app) {
      setSelectedApp(app);
      setFormData(prev => ({
        ...prev,
        app_id: app.id
      }));
    }
    setEditingHook(null);
    setDialogOpen(true);
  };

  const openEditDialog = (hook: WebhookHook) => {
    setFormData({
      webhook_url: hook.webhook_url || '',
      events: hook.events || [],
      app_id: hook.app_id || '',
      settings: {}
    });
    setEditingHook(hook);
    setSelectedApp(apps.find(app => app.id === hook.app_id) || null);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      webhook_url: '',
      events: [],
      app_id: '',
      settings: {}
    });
    setSelectedApp(null);
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEventToggle = (eventId: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      events: checked
        ? [...prev.events, eventId]
        : prev.events.filter(id => id !== eventId)
    }));
  };

  const groupEventsByCategory = () => {
    const categories: Record<string, typeof availableEvents> = {};
    availableEvents.forEach(event => {
      if (!categories[event.category]) {
        categories[event.category] = [];
      }
      categories[event.category].push(event);
    });
    return categories;
  };

  useEffect(() => {
    loadApps();
    loadHooks();
  }, []);

  return (
    <Box p={3}>
      <Typography variant="h4" component="h1" mb={3}>
        Integrations
      </Typography>

      <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab label="Available Apps" />
        <Tab label="Configured Webhooks" />
      </Tabs>

      {tabValue === 0 && (
        <Box>
          <Typography variant="h6" mb={2}>
            Available Integration Apps
          </Typography>
          <Grid container spacing={3}>
            {apps.map((app) => (
              <Grid item xs={12} md={6} lg={4} key={app.id}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Avatar 
                        src={app.logo_url} 
                        sx={{ width: 40, height: 40 }}
                      >
                        <Webhook />
                      </Avatar>
                      <Box>
                        <Typography variant="h6">
                          {app.name}
                        </Typography>
                        <Chip 
                          label={app.category}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      {app.description}
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                      {app.webhook_support && (
                        <Chip label="Webhooks" size="small" color="primary" />
                      )}
                      <Chip 
                        label={`${app.supported_events.length} events`} 
                        size="small" 
                        color="secondary" 
                      />
                    </Box>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => openCreateDialog(app)}
                      disabled={!app.webhook_support}
                      startIcon={<Add />}
                    >
                      Configure
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {tabValue === 1 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Configured Webhooks
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => openCreateDialog()}
            >
              Add Webhook
            </Button>
          </Box>

          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>App</TableCell>
                    <TableCell>Webhook URL</TableCell>
                    <TableCell>Events</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hooks.map((hook) => (
                    <TableRow key={hook.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar sx={{ width: 32, height: 32 }}>
                            <Webhook />
                          </Avatar>
                          <Typography variant="body2">
                            {hook.app_id || 'Generic Webhook'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {hook.webhook_url}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {hook.events.length} events
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={hook.active ? 'Active' : 'Inactive'}
                          size="small"
                          color={hook.active ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleTestHook(hook.id)}
                          title="Test Webhook"
                        >
                          <PlayArrow />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(hook)}
                          title="Edit Hook"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteHook(hook.id)}
                          title="Delete Hook"
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingHook ? 'Edit Integration Hook' : 'Create Integration Hook'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} mt={2}>
            {selectedApp && (
              <Alert severity="info">
                <Typography variant="body2">
                  Configuring webhook for {selectedApp.name}
                </Typography>
              </Alert>
            )}

            <TextField
              label="Webhook URL"
              value={formData.webhook_url}
              onChange={(e) => handleFormChange('webhook_url', e.target.value)}
              fullWidth
              required
              placeholder="https://your-app.com/webhooks/converse"
              helperText="Enter the URL where you want to receive webhook events"
            />

            <Divider />

            <FormLabel component="legend">
              Select Events to Subscribe
            </FormLabel>

            {Object.entries(groupEventsByCategory()).map(([category, events]) => (
              <Box key={category}>
                <Typography variant="subtitle2" color="primary" mb={1}>
                  {category}
                </Typography>
                <FormGroup row>
                  {events
                    .filter(event => !selectedApp || selectedApp.supported_events.includes(event.id))
                    .map((event) => (
                      <FormControlLabel
                        key={event.id}
                        control={
                          <Checkbox
                            checked={formData.events.includes(event.id)}
                            onChange={(e) => handleEventToggle(event.id, e.target.checked)}
                          />
                        }
                        label={event.label}
                      />
                    ))}
                </FormGroup>
              </Box>
            ))}

            {formData.events.length === 0 && (
              <Alert severity="warning">
                Please select at least one event to subscribe to.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={editingHook ? handleUpdateHook : handleCreateHook}
            variant="contained"
            disabled={!formData.webhook_url || formData.events.length === 0}
          >
            {editingHook ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Result Dialog */}
      {testResult && (
        <Dialog open={Boolean(testResult)} onClose={() => setTestResult(null)} maxWidth="sm" fullWidth>
          <DialogTitle>
            Webhook Test Result
          </DialogTitle>
          <DialogContent>
            <Alert severity={testResult.success ? 'success' : 'error'} sx={{ mb: 2 }}>
              {testResult.message}
            </Alert>
            {testResult.status_code && (
              <Typography variant="body2" mb={1}>
                <strong>Status Code:</strong> {testResult.status_code}
              </Typography>
            )}
            {testResult.response_body && (
              <Box>
                <Typography variant="body2" mb={1}>
                  <strong>Response:</strong>
                </Typography>
                <Paper sx={{ p: 2, backgroundColor: 'grey.100' }}>
                  <Typography variant="body2" fontFamily="monospace" fontSize="0.875rem">
                    {testResult.response_body}
                  </Typography>
                </Paper>
              </Box>
            )}
            {testResult.error && (
              <Box mt={2}>
                <Typography variant="body2" mb={1}>
                  <strong>Error:</strong>
                </Typography>
                <Paper sx={{ p: 2, backgroundColor: 'error.light', color: 'error.contrastText' }}>
                  <Typography variant="body2" fontFamily="monospace" fontSize="0.875rem">
                    {testResult.error}
                  </Typography>
                </Paper>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTestResult(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IntegrationsPage;